import { useEffect, useState } from "react";
import { formatDateTime } from "../../utils/FormatUtils";
import {
  getPatientTreatmentSheetHistory,
  inPhieuHoSoBenhAn,
} from "../../phan-he-noi-tru-dieu-tri/services/BenhAnServices";
import { THONG_TIN_BENH_AN_CONFIG } from "../../phan-he-noi-tru-dieu-tri/components/benh-an/constant";
import { toast } from "react-toastify";
import { inToDieuTri } from "../../phan-he-kham-benh/services/PrintExportService";
import LazyLoadTable from "../lazy-load-table/LazyLoadTable";
import "./LichSuPhieuDieuTri.scss";

interface LichSuPhieuDieuTriProps {
  patientId?: string;
}

const LichSuPhieuDieuTri = ({ patientId }: LichSuPhieuDieuTriProps) => {
  const [treatmentSheets, setTreatmentSheets] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState<any>(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [loadingPdf, setLoadingPdf] = useState(false);

  console.log("LichSuPhieuDieuTri component - patientId:", patientId);

  useEffect(() => {
    console.log("useEffect triggered with patientId:", patientId);
    if (patientId) fetchTreatmentSheets();
  }, [patientId]);

  const fetchTreatmentSheets = async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const response = await getPatientTreatmentSheetHistory(patientId);
      console.log("Treatment sheets response:", response);
      const responseCode = (response?.data as any)?.code;
      if (responseCode === "OK" || responseCode === 200) {
        const sheets = (response.data.data || []).map((sheet: any, index: number) => ({
          ...sheet,
          uniqueId: sheet.id || sheet.encounterId || `sheet-${index}`
        }));
        console.log("Treatment sheets data:", sheets);
        setTreatmentSheets(sheets);
      }
    } catch (error) {
      console.error("Error fetching treatment sheets:", error);
      toast.error("Không thể tải lịch sử");
    } finally {
      setLoading(false);
    }
  };

  const getSheetType = (sheet: any) => {
    // Phân loại dựa vào source và treatmentType
    if (sheet.source === "CIS") {
      return "Khám bệnh";
    }

    // Phiếu điều trị: treatmentType 1-3 = Nội trú, 4-6 = Ngoại trú
    if (sheet.treatmentType) {
      const isInpatient = [1, 2, 3].includes(sheet.treatmentType);
      return isInpatient ? "ĐT Nội trú" : "ĐT Ngoại trú";
    }

    // Fallback cho data cũ chưa có treatmentType
    return sheet.source === "INPATIENT" ? "ĐT Nội trú" : "Khám bệnh";
  };

  const getSheetBadgeColor = (sheet: any) => {
    const type = getSheetType(sheet);
    if (type === "Khám bệnh") return "bg-success";
    if (type === "ĐT Ngoại trú") return "bg-info";
    return "bg-primary"; // ĐT Nội trú
  };

  const handleRowClick = async (sheet: any) => {
    try {
      console.log("handleRowClick - sheet:", sheet);
      setSelectedSheet(sheet);
      setLoadingPdf(true);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);

      let response;
      const sheetType = getSheetType(sheet);
      console.log("sheetType:", sheetType);

      // Phân loại theo loại phiếu - TẬP TRUNG VÀO KHÁM BỆNH
      if (sheetType === "Khám bệnh") {
        // Khám bệnh (CIS) - gọi API medical-exam/export/treatment-sheet
        // Dùng orderId từ API response (đã được map từ backend)
        const orderId = sheet.orderId || sheet.visitId || sheet.encounterId;

        const params = {
          orderId: orderId,
          fileType: "pdf",
        };
        console.log("[KHÁM BỆNH] Calling inToDieuTri with orderId:", orderId, "full sheet:", sheet);
        response = await inToDieuTri(params);
      } else if (sheetType === "ĐT Ngoại trú") {
        // Ngoại trú - gọi API medical-exam/export/treatment-sheet
        const orderId = sheet.visitId || sheet.id;
        const params = {
          orderId: orderId,
          fileType: "pdf",
        };
        console.log("[NGOẠI TRÚ] Calling inToDieuTri with orderId:", orderId);
        response = await inToDieuTri(params);
      } else {
        // ĐT Nội trú - gọi API reports/run/5d1aec65-73c6-245a-b412-7e01a2008893
        const params = {
          keyConfig: THONG_TIN_BENH_AN_CONFIG.PHIEU_DIEU_TRI.KEY,
          exportId: "5d1aec65-73c6-245a-b412-7e01a2008893",
          parameters: [
            {
              name: THONG_TIN_BENH_AN_CONFIG.PHIEU_DIEU_TRI.EXPORT_CODE,
              value: sheet.id,
            },
          ],
          fileType: "pdf",
        };
        console.log("Calling inPhieuHoSoBenhAn (Nội trú) with params:", params);
        response = await inPhieuHoSoBenhAn(params);
      }

      setPdfUrl(
        URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        )
      );
    } catch (error) {
      toast.error("Không thể xem phiếu");
    } finally {
      setLoadingPdf(false);
    }
  };

  useEffect(
    () => () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl]
  );

  // Define columns for LazyLoadTable
  const columns = [
    {
      name: "STT",
      field: "stt",
      width: 50,
      render: (_: any, index: number) => index + 1,
      align: "center",
    },
    {
      name: "Thời gian",
      field: "prescriptionDate",
      width: 150,
      render: (row: any) =>
        formatDateTime(row.prescriptionDate || row.encounterDatetime),
    },
    {
      name: "Loại",
      field: "source",
      width: 120,
      render: (row: any) => (
        <span className={`badge ${getSheetBadgeColor(row)}`}>
          {getSheetType(row)}
        </span>
      ),
      align: "center",
    },
    {
      name: "Mã BA",
      field: "medicalRecordCode",
      width: 120,
      render: (row: any) => row.medicalRecordCode || "-",
    },
  ];

  return (
    <div className="lich-su-phieu-dieu-tri-container spaces px-16 py-5">
      <div className="row flex-grow-1" style={{ minHeight: 0 }}>
        <div className="col-md-5 pe-2" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="card flex-grow-1" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
            <div className="card-body p-0 flex-grow-1" style={{ minHeight: 0, overflow: "hidden" }}>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <LazyLoadTable
                  columns={columns}
                  axiosReq={() => Promise.resolve({
                    data: {
                      code: 200,
                      data: treatmentSheets
                    },
                    status: 200,
                    statusText: 'OK',
                    headers: {},
                    config: {} as any
                  })}
                  searchData={{ patientId }}
                  onRowClick={handleRowClick}
                  rowSeclected={selectedSheet}
                  rowKey="uniqueId"
                  urlData="data.data"
                  isToggle={true}
                  dependencies={[treatmentSheets]}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-md-7 ps-2" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="card flex-grow-1" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
            <div className="card-body p-0 flex-grow-1" style={{ minHeight: 0 }}>
              {loadingPdf ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <div className="spinner-border text-primary" />
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  title="PDF"
                />
              ) : (
                <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                  <div className="text-center">
                    <i
                      className="bi bi-file-pdf"
                      style={{ fontSize: "6rem" }}
                    />
                    <p className="mt-3">Chọn một phiếu để xem nội dung</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LichSuPhieuDieuTri;
